/**
 * ベース地図のソース切り替え。
 *
 * docs/04 の方針:
 *   - 既定は **PMTiles（オフライン・単一ファイル）**。通信は最初の1回だけ
 *   - 地理院タイルは**オンライン時のみのオプション**（出典表示が必須）
 *   - どちらも無い状態でも**アプリは動く**（航跡・地点は自前で描くため）
 *
 * OSM 公式タイルサーバーは Tile Usage Policy でバルクダウンロードが禁止されているため、
 * 事前キャッシュ（PMTilesの元データ）用途では**使わない**。一方、地理院タイルは
 * 日本国内しかカバーしないため、海外の会場（例: 世界選手権クロスノ）では
 * オンライン地図が1つも選べなくなってしまう。そのためOSMタイルは
 * 「毎回その場で読むだけ・保存しない」オンライン専用オプションとして別途用意する
 * （バルクダウンロードではなく通常のオンライン閲覧なので Tile Usage Policy に反しない）。
 *
 * 地理院の「写真」（航空写真）に相当する世界全域の衛星写真は、OSM自体には
 * 無い（OSMはベクタの地図データのみで衛星画像を持たない）ため、
 * Esri World Imagery（登録不要・無償で使える世界全域の衛星写真タイル。
 * OSSの地図アプリで広く使われている定番ソース）を使う。
 */

import type { StyleSpecification } from 'maplibre-gl';

export type BasemapId = 'none' | 'gsi-pale' | 'gsi-photo' | 'osm' | 'satellite' | 'pmtiles';

export interface BasemapOption {
  id: BasemapId;
  label: string;
  /** オフラインで使えるか */
  offline: boolean;
  note?: string;
}

export const BASEMAPS: BasemapOption[] = [
  { id: 'none', label: '地図なし', offline: true, note: '航跡と地点だけを表示' },
  {
    id: 'pmtiles',
    label: 'オフライン地図 (PMTiles)',
    offline: true,
    note: '会場ファイルを事前取得して使用',
  },
  { id: 'gsi-pale', label: '地理院 淡色', offline: false, note: '通信あり・出典表示必須（国内のみ）' },
  { id: 'gsi-photo', label: '地理院 写真', offline: false, note: '通信あり・出典表示必須（国内のみ）' },
  { id: 'osm', label: 'OpenStreetMap', offline: false, note: '通信あり・出典表示必須（世界全域、国外の会場向け）' },
  { id: 'satellite', label: '衛星写真', offline: false, note: '通信あり・出典表示必須（世界全域、国外の会場向け）' },
];

const BACKGROUND = '#0e1520';

/**
 * 空（地平線から上）。
 *
 * カメラを寝かせられるようにした（`MAX_PITCH`）ため、地平線が視界に入る。
 * これが無いと地平線から上が背景色のベタ塗りになり、空と地面の区別が付かない。
 *
 * ★ `fog-*` は使わない。3D terrain（DEM）が要るが、このアプリは標高データを
 *   持たず、高さは GPS 高度を fill-extrusion で描く方式のため。
 *
 * ★ 空の色は `mapTheme`（PMTiles の配色モード）ではなく、**そのスタイル自身の
 *   地の色**に合わせる。地理院タイルは固定の絵で mapTheme の対象外であり、
 *   「地理院タイル・地図なしは mapTheme の影響を受けない」という既存の
 *   取り決めを崩さないため。
 */
type Sky = { 'sky-color': string; 'horizon-color': string; 'sky-horizon-blend': number };

/** 暗い地図（屋外配色・地図なし）に合わせた夜寄りの空 */
const DARK_SKY: Sky = {
  'sky-color': '#0a1020',
  'horizon-color': '#2b3d52',
  'sky-horizon-blend': 0.6,
};

/** 明るい地図（屋内配色・地理院タイル）に合わせた昼の空 */
const LIGHT_SKY: Sky = {
  'sky-color': '#9fc4e8',
  'horizon-color': '#dfe8f0',
  'sky-horizon-blend': 0.7,
};

/**
 * PMTiles の配色モード。
 *
 * 「屋外」は直射日光下でも画面が白飛びしにくいダーク基調の配色。
 * 「屋内」は室内の照明下で見やすい、白系の紙地図に近い明るい配色（既定）。
 * どちらが読みやすいかは光の条件次第なので選べるようにしてある
 * （本人指摘により屋外/屋内を入れ替え、2026-08-09）。
 */
export type MapTheme = 'indoor' | 'outdoor';

const PMTILES_PALETTES: Record<
  MapTheme,
  {
    background: string;
    water: string;
    landuse: string;
    landuseOpacity: number;
    roads: string;
    buildings: string;
    sky: Sky;
  }
> = {
  indoor: {
    background: '#eef1ee',
    water: '#a9cbe0',
    landuse: '#e3e7dd',
    landuseOpacity: 0.7,
    roads: '#767c85',
    buildings: '#d7d2c5',
    sky: LIGHT_SKY,
  },
  outdoor: {
    background: '#0e1520',
    water: '#16324a',
    landuse: '#152131',
    landuseOpacity: 0.6,
    roads: '#2c3b4f',
    buildings: '#1d2a3a',
    sky: DARK_SKY,
  },
};

/** 何も敷かない下地。オフラインでも必ず表示できる最後の砦 */
function blankStyle(): StyleSpecification {
  return {
    version: 8,
    sky: DARK_SKY,
    sources: {},
    layers: [{ id: 'background', type: 'background', paint: { 'background-color': BACKGROUND } }],
  };
}

function gsiRasterStyle(kind: 'pale' | 'photo'): StyleSpecification {
  const url =
    kind === 'pale'
      ? 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'
      : 'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg';
  return {
    version: 8,
    // 地理院タイルは昼間の絵なので、mapTheme とは無関係に明るい空を敷く
    sky: LIGHT_SKY,
    sources: {
      gsi: {
        type: 'raster',
        tiles: [url],
        tileSize: 256,
        maxzoom: kind === 'pale' ? 18 : 18,
        // 利用規約上、出典の明示は必須
        attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': BACKGROUND } },
      {
        id: 'gsi',
        type: 'raster',
        source: 'gsi',
        paint: { 'raster-opacity': kind === 'pale' ? 1 : 0.9 },
      },
    ],
  };
}

/**
 * OpenStreetMap 標準タイル（世界全域）。
 *
 * 地理院タイルは日本国内しかカバーしないため、海外の会場（世界選手権
 * クロスノ等）で地図なし/PMTiles未取得のときにオンライン地図が
 * 1つも選べなくなる問題への対応。バルクダウンロードではなく通常の
 * オンライン閲覧のみなので Tile Usage Policy に反しない（ファイル先頭の
 * コメント参照）。
 */
function osmRasterStyle(): StyleSpecification {
  return {
    version: 8,
    sky: LIGHT_SKY,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 19,
        attribution: '<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': BACKGROUND } },
      { id: 'osm', type: 'raster', source: 'osm' },
    ],
  };
}

/**
 * 衛星写真（世界全域）。地理院の「写真」に相当するものが国外では
 * 選べないため、Esri World Imagery（登録不要・無償、OSSの地図アプリで
 * 広く使われている）で代替する。
 *
 * ★ Esriのタイルサービスは `{z}/{y}/{x}` の順（MapLibreの既定の
 *   `{z}/{x}/{y}`とは順序が違う）。URLテンプレートの文字列置換なので、
 *   ここを間違えるとタイルが読めない（座標が入れ替わって存在しない
 *   パスを叩くことになる）。
 */
function satelliteRasterStyle(): StyleSpecification {
  return {
    version: 8,
    sky: LIGHT_SKY,
    sources: {
      satellite: {
        type: 'raster',
        tiles: [
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        ],
        tileSize: 256,
        maxzoom: 19,
        attribution: 'Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': BACKGROUND } },
      { id: 'satellite', type: 'raster', source: 'satellite' },
    ],
  };
}

/**
 * PMTiles のベクタ地図。
 * 会場ごとの `.pmtiles` を事前に取得して置く運用（docs/04 §2）。
 */
function pmtilesStyle(url: string, theme: MapTheme): StyleSpecification {
  const c = PMTILES_PALETTES[theme];
  return {
    version: 8,
    sky: c.sky,
    sources: {
      basemap: {
        type: 'vector',
        url: `pmtiles://${url}`,
        attribution: '<a href="https://openstreetmap.org">OpenStreetMap</a> contributors',
        // ★ 生成パイプラインの実データは z15 まで（上のコメント参照）。
        //   ここを省略すると MapLibre がそれより先のズームで「タイルが
        //   無い」と諦めて何も描かなくなる（z15を拡大して使い回す
        //   オーバーズームをしてくれない）。長押しの拡大鏡のように
        //   z15よりずっと先まで寄ることがあるので、必ず明示する。
        maxzoom: 15,
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': c.background } },
      /*
       * ★ `$type` が Polygon のものだけを塗ること。
       *
       * Protomaps の `water` は1つの source-layer にポリゴン（湖・海・
       * 広い川）と LineString（細い川・用水路の中心線）が混在している。
       * 実データ（渡良瀬 z15・169タイル）では Polygon 171 に対し
       * LineString が 509 本もある。MapLibre の fill レイヤーは
       * ジオメトリ種別を見ずに `classifyRings()` へ渡すため、フィルタが
       * 無いと**線が閉じられて塗り潰される**。頂点が3〜4個しかない線が
       * 178本あり、これがそのまま三角形・四角形の塗りになって現れていた
       * （「細い川が矩形の三角や四角になっている」の正体。2026-08-09、
       * 実ファイルをデコードして特定）。
       */
      {
        id: 'water',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'water',
        filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': c.water },
      },
      {
        id: 'landuse',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'landuse',
        // 渡良瀬の実データは Polygon のみだが、他会場のファイルで種別が
        // 混ざっても崩れないよう、塗る層には一律で同じ制限をかけておく
        filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': c.landuse, 'fill-opacity': c.landuseOpacity },
      },
      /*
       * 細い川・用水路は上記の LineString 側にある。塗りから除いた分、
       * ここで線として描き直す。kind は実データに出てくる5種
       * （river / canal / stream / drain / ditch）を拾う。
       * landuse の後に描き、農地・林の塗りに隠れないようにする。
       */
      {
        id: 'waterway-river',
        type: 'line',
        source: 'basemap',
        'source-layer': 'water',
        filter: ['all', ['==', '$type', 'LineString'], ['in', 'kind', 'river', 'canal']],
        minzoom: 9,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': c.water,
          'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 9, 0, 9.5, 1, 18, 12],
        },
      },
      {
        id: 'waterway-stream',
        type: 'line',
        source: 'basemap',
        'source-layer': 'water',
        filter: ['all', ['==', '$type', 'LineString'], ['in', 'kind', 'stream', 'drain', 'ditch']],
        // 用水路は着陸地点の判断や回収路の目印になるので、公式スタイルの
        // z14 より少し早く出す
        minzoom: 13,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': c.water,
          'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.5, 18, 1.6],
        },
      },
      {
        id: 'roads',
        type: 'line',
        source: 'basemap',
        'source-layer': 'roads',
        paint: { 'line-color': c.roads, 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.4, 16, 2.5] },
      },
      {
        id: 'buildings',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'buildings',
        // buildings にも点データが少数混ざっている。water ほどの実害は
        // 無いが、fill に渡す以上は同じ理由で種別を絞っておく
        filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': c.buildings },
      },
    ],
  };
}

export interface BasemapContext {
  /** PMTiles ファイルの URL（会場ごと）。無ければ PMTiles は選べない */
  pmtilesUrl?: string;
  /** PMTiles の配色。省略時は屋内（既定の明るい配色） */
  mapTheme?: MapTheme;
}

export function buildStyle(id: BasemapId, ctx: BasemapContext = {}): StyleSpecification {
  switch (id) {
    case 'gsi-pale':
      return gsiRasterStyle('pale');
    case 'gsi-photo':
      return gsiRasterStyle('photo');
    case 'osm':
      return osmRasterStyle();
    case 'satellite':
      return satelliteRasterStyle();
    case 'pmtiles':
      return ctx.pmtilesUrl ? pmtilesStyle(ctx.pmtilesUrl, ctx.mapTheme ?? 'indoor') : blankStyle();
    default:
      return blankStyle();
  }
}
